import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchRenderById, incrementViewCount } from '../api/renders';
import { fetchComments, postComment } from '../api/comments';
import CommentSection from '../components/CommentSection';
import '../styles/RenderPage.css';

const RenderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [render, setRender] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewsUpdated, setViewsUpdated] = useState(false);

  useEffect(() => {
    const loadRender = async () => {
      try {
        const renderData = await fetchRenderById(id);
        if (!renderData) {
          setError('Render not found');
          setLoading(false);
          return;
        }
        
        setRender(renderData);
        
        // Fetch comments
        const commentsData = await fetchComments(id);
        setComments(commentsData);
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch render:', err);
        setError('Failed to load this render. Please try again later.');
        setLoading(false);
      }
    };

    loadRender();
  }, [id]);

  // Increment view count once when the page loads
  useEffect(() => {
    const updateViewCount = async () => {
      if (render && !viewsUpdated) {
        try {
          const updatedRender = await incrementViewCount(id);
          setRender(updatedRender);
          setViewsUpdated(true);
        } catch (err) {
          console.error('Failed to update view count:', err);
          // Non-critical error, don't display to user
        }
      }
    };

    updateViewCount();
  }, [render, id, viewsUpdated]);

  const handleCommentSubmit = async (commentText) => {
    try {
      const newComment = await postComment(id, commentText);
      setComments(prevComments => [newComment, ...prevComments]);
      return true;
    } catch (error) {
      console.error('Failed to post comment:', error);
      return false;
    }
  };

  if (loading) {
    return (
      <div className="render-page loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading render...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="render-page error">
        <div className="error-message">
          <h2>Oops! Something went wrong</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Return to Gallery</button>
        </div>
      </div>
    );
  }

  const { title, fullImage, description, createdAt, software, renderTime, views } = render;

  // Format the date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="render-page">
      <div className="render-header">
        <h1>{title}</h1>
        <button className="back-button" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"></line>
            <polyline points="12 19 5 12 12 5"></polyline>
          </svg>
          Back to Gallery
        </button>
      </div>

      <div className="render-content">
        <div className="render-image-container">
          <img src={fullImage} alt={title} className="render-image" />
          
          <div className="render-stats">
            <div className="stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
              <span>{views} views</span>
            </div>
            
            <div className="stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <span>{formatDate(createdAt)}</span>
            </div>
            
            <div className="stat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <span>{renderTime || 'N/A'}</span>
            </div>
          </div>
        </div>

        <div className="render-details">
          <div className="render-description">
            <h2>About this render</h2>
            <p>{description}</p>
          </div>
          
          <div className="render-metadata">
            <h3>Technical Details</h3>
            <div className="metadata-item">
              <span className="metadata-label">Software:</span>
              <span className="metadata-value">{software || 'Blender'}</span>
            </div>
            {renderTime && (
              <div className="metadata-item">
                <span className="metadata-label">Render Time:</span>
                <span className="metadata-value">{renderTime}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <CommentSection 
        comments={comments} 
        onCommentSubmit={handleCommentSubmit} 
      />
    </div>
  );
};

export default RenderPage;